import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { FormDefinition, Question, Section, SectionFlowState } from '../../types/form-engine.types';
import { validateRequiredAnswers } from '../../utils/form-validation';
import {
  createSectionFlowState,
  goNext,
  goPrevious,
  goToSection,
  reopenEndedForm,
  validateAllVisited,
} from '../../utils/section-flow';
import { QuestionAnswerField } from '../question-answer-field/question-answer-field';

export interface FormRunnerProps {
  value: FormDefinition;
  onChange: (value: FormDefinition) => void;
  /** Called with every question (skipped sections included, as the legacy backend expects) once validation passes. */
  onSubmit: (questions: Question[]) => void | Promise<void>;
  readOnly?: boolean;
  canClearAnswers?: boolean;
  isSubmitting?: boolean;
  /** Extra content rendered above the questions (e.g. project selection). */
  header?: React.ReactNode;
}

const replaceSection = (sections: Section[], sectionId: string, question: Question) =>
  sections.map((section) =>
    section.SectionId === sectionId
      ? { ...section, Questions: section.Questions.map((q) => (q.ItemId === question.ItemId ? question : q)) }
      : section
  );

export const FormRunner = ({ value, onChange, onSubmit, readOnly, canClearAnswers, isSubmitting, header }: FormRunnerProps) => {
  const { t } = useTranslation();
  const [flow, setFlow] = useState<SectionFlowState>(() => createSectionFlowState(value.Sections));
  const [errors, setErrors] = useState<string[]>([]);

  const sections = value.Sections;
  const current = sections[flow.currentSectionIndex];
  const isLast = flow.currentSectionIndex === sections.length - 1;

  const changeQuestion = (question: Question, sectionId?: string) =>
    onChange(
      value.HasSections && sectionId
        ? { ...value, Sections: replaceSection(sections, sectionId, question) }
        : { ...value, FormData: value.FormData.map((q) => (q.ItemId === question.ItemId ? question : q)) }
    );

  const submitFlat = () => {
    const found = validateRequiredAnswers(value.FormData);
    setErrors(found);
    if (!found.length) void onSubmit(value.FormData);
  };

  const submitSectioned = () => {
    const result = validateAllVisited(flow, sections);
    setFlow(result.state);
    setErrors(result.errors);
    if (!result.errors.length) void onSubmit(sections.flatMap((section) => section.Questions));
  };

  const handleNext = () => {
    const result = goNext(flow, sections);
    setFlow(result.state);
    setErrors(result.errors);
  };

  const errorList = errors.length > 0 && (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
      <p className="mb-1 font-medium">{t('PLEASE_FIX_ERRORS')}</p>
      <ul className="list-disc pl-5">
        {errors.map((error) => (
          <li key={error}>{error}</li>
        ))}
      </ul>
    </div>
  );

  if (!value.HasSections) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        {value.FormData.map((question) => (
          <QuestionAnswerField
            key={question.ItemId}
            question={question}
            readOnly={readOnly}
            canClear={canClearAnswers}
            onChange={(next) => changeQuestion(next)}
          />
        ))}
        {errorList}
        {!readOnly && (
          <div className="flex justify-end">
            <Button type="button" loading={isSubmitting} disabled={isSubmitting} onClick={submitFlat}>
              {t('SUBMIT')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {header}

      <ol className="flex flex-wrap gap-2 text-sm">
        {sections.map((section, index) => {
          const skipped = flow.skippedSections.includes(section.SectionId);
          const active = index === flow.currentSectionIndex;
          return (
            <li key={section.SectionId}>
              <button
                type="button"
                disabled={skipped && !flow.isFormEnded}
                onClick={() => setFlow(goToSection(flow, sections, index))}
                className={`rounded-full border px-3 py-1 ${active ? 'border-primary bg-primary/10 font-medium' : ''} ${skipped ? 'opacity-40 line-through' : ''}`}
              >
                {index + 1}. {section.Title || `${t('SECTION')} ${index + 1}`}
              </button>
            </li>
          );
        })}
      </ol>

      {current && (
        <div className="flex flex-col gap-4">
          {current.Description && <p className="text-sm text-muted-foreground">{current.Description}</p>}
          {current.Questions.map((question) => (
            <QuestionAnswerField
              key={question.ItemId}
              question={question}
              readOnly={readOnly}
              canClear={canClearAnswers}
              onChange={(next) => changeQuestion(next, current.SectionId)}
            />
          ))}
        </div>
      )}

      {flow.isFormEnded && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm">
          <span>{t('FORM_ENDED_BASED_ON_SELECTION')}</span>
          <Button type="button" variant="outline" size="sm" onClick={() => setFlow(reopenEndedForm(flow, sections))}>
            {t('BACK')}
          </Button>
        </div>
      )}

      {errorList}

      {!readOnly && (
        <div className="flex justify-between gap-3">
          <Button type="button" variant="outline" disabled={flow.currentSectionIndex === 0} onClick={() => setFlow(goPrevious(flow))}>
            {t('PREVIOUS')}
          </Button>
          {isLast || flow.isFormEnded ? (
            <Button type="button" loading={isSubmitting} disabled={isSubmitting} onClick={submitSectioned}>
              {t('SUBMIT')}
            </Button>
          ) : (
            <Button type="button" onClick={handleNext}>
              {t('NEXT')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
